import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSOES } from '../constants/permissions';
import Spinner from '../components/ui/Spinner';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { motion } from 'framer-motion';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: string[]; // ✅ Make permissions optional
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  permissions = [] // ✅ Default to empty array if undefined
}) => {
  const { usuario, carregandoAutenticacao } = useAuth();
  const { hasPermission } = usePermissions();
  const location = useLocation();

  const [verificandoPermissoes, setVerificandoPermissoes] = React.useState(true);
  const [autorizado, setAutorizado] = React.useState(false);

  useEffect(() => {
    if (carregandoAutenticacao) {
      return;
    }

    if (!usuario) {
      setAutorizado(false);
      setVerificandoPermissoes(false);
      return;
    }

    // ✅ Safe check: ensure permissions is always an array
    const safePermissions = Array.isArray(permissions) ? permissions : [];
    
    // If no permissions required, automatically authorize
    if (safePermissions.length === 0) {
      setAutorizado(true);
      setVerificandoPermissoes(false);
      return;
    }

    // ✅ Safe permission check
    const todasPermissoesConcedidas = safePermissions.every(perm => 
      perm && hasPermission(perm)
    );

    setAutorizado(todasPermissoesConcedidas);
    setVerificandoPermissoes(false);

  }, [usuario, carregandoAutenticacao, permissions, hasPermission]);

  if (carregandoAutenticacao || verificandoPermissoes) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (!autorizado) {
    if (!usuario) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    } else {
      return (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center min-h-screen bg-background p-4"
        >
          <Card className="max-w-md w-full text-center p-8 shadow-xl border border-primary/20">
            <h2 className="text-3xl font-bold text-danger mb-4">Acesso Negado!</h2>
            <p className="text-text-light mb-6">
              Você não possui as permissões necessárias para acessar esta página.
            </p>
            <Button onClick={() => window.history.back()} tipo="secondary" className="w-full">
              Voltar
            </Button>
          </Card>
        </motion.div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;